/**
 * CR401: Deal Cards Admin Page
 * CR318: Session gating, DB fallback, visible errors
 * 
 * Reorganized information architecture with two top-level tabs:
 * - Templates: List/grid of templates by deal_type_key × variant_key
 * - Deal Types: Deal type registry metadata (no ribbon controls)
 * 
 * Template Editor is now a standalone route: /admin/deal-cards/templates/:deal_type_key
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  CreditCard, 
  RefreshCw,
  Layers,
  LayoutGrid,
} from "lucide-react";
import { TemplatesListTab } from "@/components/admin/deal-cards/TemplatesListTab";
import { DealTypesTab } from "@/components/admin/deal-cards/DealTypesTab";
import type { DealTypeRegistryEntry, DealCardTemplate } from "@/providers/DealCardConfigProvider";

type DataSource = "edge" | "db-fallback" | null;

export default function DealCardsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Data state
  const [registry, setRegistry] = useState<DealTypeRegistryEntry[]>([]);
  const [templates, setTemplates] = useState<DealCardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<DataSource>(null);
  const [authRequired, setAuthRequired] = useState(false);
  
  // Prevent double-fetch on mount
  const hasFetchedRef = useRef(false);
  
  // Tab state - default to 'templates' per CR401 requirements
  const [activeTab, setActiveTab] = useState<string>(() => {
    return searchParams.get("tab") || "templates";
  });

  // DB fallback: load directly from tables (registry-driven)
  const fetchFromDB = useCallback(async (): Promise<{ registry: DealTypeRegistryEntry[]; success: boolean }> => {
    console.debug("[DealCardsPage] load:fallback_start");
    
    try {
      // Query registry (driver dataset)
      const { data: regData, error: regError } = await supabase
        .from("deal_type_registry")
        .select("deal_type_key, is_enabled, label, ribbon_text, ribbon_theme, sort_order, updated_at, updated_by, show_flyer_evidence")
        .order("sort_order", { ascending: true });

      if (regError) {
        console.error("[DealCardsPage] load:fallback_registry_error", regError);
        return { registry: [], success: false };
      }

      // Query visibility rules
      const { data: visData, error: visError } = await supabase
        .from("deal_type_visibility_rules")
        .select("deal_type, show_in_staging, show_in_admin_prod, show_in_public, updated_at, updated_by");

      if (visError) {
        console.warn("[DealCardsPage] load:fallback_visibility_warn", visError);
        // Continue without visibility data - registry is the driver
      }

      // Build visibility map keyed by deal_type
      const visMap = new Map<string, {
        show_in_staging: boolean;
        show_in_admin_prod: boolean;
        show_in_public: boolean;
      }>();
      
      (visData || []).forEach((v: any) => {
        visMap.set(v.deal_type, {
          show_in_staging: v.show_in_staging ?? true,
          show_in_admin_prod: v.show_in_admin_prod ?? false,
          show_in_public: v.show_in_public ?? false,
        });
      });

      // Merge: registry-driven with left-join to visibility
      const merged: DealTypeRegistryEntry[] = (regData || []).map((r: any) => {
        const vis = visMap.get(r.deal_type_key) || {
          show_in_staging: true,
          show_in_admin_prod: false,
          show_in_public: false,
        };
        
        return {
          deal_type_key: r.deal_type_key,
          is_enabled: r.is_enabled ?? true,
          label: r.label || r.deal_type_key,
          ribbon_text: r.ribbon_text || r.deal_type_key,
          ribbon_theme: r.ribbon_theme || "gray",
          sort_order: r.sort_order ?? 999,
          updated_at: r.updated_at,
          updated_by: r.updated_by,
          show_flyer_evidence: r.show_flyer_evidence ?? true,
          // Visibility fields (for future use)
          show_in_staging: vis.show_in_staging,
          show_in_admin_prod: vis.show_in_admin_prod,
          show_in_public: vis.show_in_public,
        };
      });

      console.debug("[DealCardsPage] load:fallback_success", { registryCount: merged.length });
      return { registry: merged, success: true };
    } catch (err) {
      console.error("[DealCardsPage] load:fallback_error", err);
      return { registry: [], success: false };
    }
  }, []);

  // Main fetch with edge function + DB fallback
  const fetchData = useCallback(async () => {
    console.debug("[DealCardsPage] load:start", { attempt: "edge" });
    setLoading(true);
    setError(null);
    setDataSource(null);
    
    try {
      // Step 1: Check session first
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData?.session) {
        console.warn("[DealCardsPage] No session - auth required");
        setAuthRequired(true);
        setLoading(false);
        return;
      }
      
      setAuthRequired(false);

      // Step 2: Try edge function
      const { data, error: fetchError } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "GET",
      });
      
      if (fetchError) {
        console.error("[DealCardsPage] load:edge_error", fetchError);
        const errorMsg = fetchError.message || "Edge function unavailable";
        
        // Step 3: Attempt DB fallback
        const fallback = await fetchFromDB();
        
        if (fallback.success && fallback.registry.length > 0) {
          setRegistry(fallback.registry);
          setTemplates([]); // Templates not available in fallback
          setDataSource("db-fallback");
          setError(`Edge function failed: ${errorMsg}. Showing data from DB fallback.`);
          toast.warning("Loaded from database fallback");
        } else {
          setError(errorMsg);
          toast.error(errorMsg);
        }
        return;
      }
      
      if (!data || !data.registry) {
        console.error("[DealCardsPage] Invalid response - missing registry:", data);
        
        // Try DB fallback
        const fallback = await fetchFromDB();
        
        if (fallback.success && fallback.registry.length > 0) {
          setRegistry(fallback.registry);
          setTemplates([]);
          setDataSource("db-fallback");
          setError("Invalid edge response. Showing data from DB fallback.");
          toast.warning("Loaded from database fallback");
        } else {
          const errorMsg = "Invalid response from server - missing registry data";
          setError(errorMsg);
          toast.error(errorMsg);
        }
        return;
      }
      
      // Success from edge function
      setRegistry(data.registry || []);
      setTemplates(data.templates || []);
      setDataSource("edge");
      setError(null);
      console.debug("[DealCardsPage] load:edge_success", { 
        registryCount: data.registry?.length,
        templatesCount: data.templates?.length 
      });
      
    } catch (err) {
      console.error("[DealCardsPage] load:unexpected_error", err);
      const errorMsg = err instanceof Error ? err.message : "Unexpected error";
      
      // Try DB fallback on any error
      const fallback = await fetchFromDB();
      
      if (fallback.success && fallback.registry.length > 0) {
        setRegistry(fallback.registry);
        setTemplates([]);
        setDataSource("db-fallback");
        setError(`${errorMsg}. Showing data from DB fallback.`);
        toast.warning("Loaded from database fallback");
      } else {
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchFromDB]);

  // Session-aware initialization
  useEffect(() => {
    // Initial fetch
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData();
    }
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.debug("[DealCardsPage] auth:state_change", { event, hasSession: !!session });
      
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // Re-fetch if we previously required auth
        if (authRequired) {
          setAuthRequired(false);
          fetchData();
        }
      } else if (event === "SIGNED_OUT") {
        setAuthRequired(true);
        setRegistry([]);
        setTemplates([]);
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchData, authRequired]);

  // Handle template edit navigation
  const handleEditTemplate = useCallback((dealTypeKey: string, variantKey: string = "standard_v2") => {
    navigate(`/admin/deal-cards/templates/${dealTypeKey}/${variantKey}`);
  }, [navigate]);

  // Handle edit ribbon deep-link (opens template editor at Components → Ribbon)
  const handleEditRibbon = useCallback((dealTypeKey: string) => {
    navigate(`/admin/deal-cards/templates/${dealTypeKey}/standard_v2?tab=components&focus=ribbon`);
  }, [navigate]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <CreditCard className="h-8 w-8 text-primary" />
              Deal Cards
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage deal card templates and deal type configuration
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs - CR401: Templates first, Deal Types second */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <LayoutGrid className="h-4 w-4" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="deal-types" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Deal Types
            </TabsTrigger>
          </TabsList>

          {/* Tab: Templates */}
          <TabsContent value="templates" className="space-y-4">
            <TemplatesListTab
              templates={templates}
              registry={registry}
              loading={loading}
              onEditTemplate={handleEditTemplate}
              onRefresh={fetchData}
            />
          </TabsContent>

          {/* Tab: Deal Types (Registry metadata only) */}
          <TabsContent value="deal-types" className="space-y-4">
            <DealTypesTab
              registry={registry}
              loading={loading}
              error={error}
              dataSource={dataSource}
              authRequired={authRequired}
              onEditTemplate={handleEditTemplate}
              onEditRibbon={handleEditRibbon}
              onRefresh={fetchData}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
